import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

import {
  TABLE_HEADER_HEIGHT_CLASS,
  TABLE_ROW_HEIGHT_CLASS,
} from './DataTable/table-dimensions'

interface TableSkeletonProps {
  columns?: number
  rows?: number
  className?: string
  /** Show a header row with column labels */
  showHeader?: boolean
  /** Show action buttons on the right side of header */
  showHeaderActions?: boolean
  /** Vary the widths of skeleton cells for a more natural look */
  randomWidths?: boolean
  /**
   * Real column widths (px numbers or CSS strings) from the table that will
   * replace this skeleton. When provided, the skeleton renders a colgroup with
   * these exact widths and matches the real header/row heights, so the
   * skeleton→table swap causes zero layout shift. Overrides `columns`.
   */
  columnWidths?: Array<number | string>
  /** Draw the pagination strip; only when the real table will have one. */
  showPagination?: boolean
}

// Predefined width patterns for more realistic skeleton appearance
const CELL_WIDTH_PATTERNS = [
  ['w-32', 'w-20', 'w-24', 'w-16', 'w-28', 'w-20', 'w-24'],
  ['w-28', 'w-16', 'w-20', 'w-24', 'w-16', 'w-28', 'w-20'],
  ['w-36', 'w-24', 'w-16', 'w-20', 'w-24', 'w-16', 'w-28'],
  ['w-24', 'w-20', 'w-28', 'w-16', 'w-20', 'w-24', 'w-16'],
  ['w-20', 'w-28', 'w-24', 'w-20', 'w-16', 'w-20', 'w-24'],
]

const HEADER_WIDTHS = ['w-20', 'w-16', 'w-24', 'w-14', 'w-18', 'w-20', 'w-16']

export function TableSkeleton({
  columns = 5,
  rows = 6,
  className,
  showHeader = true,
  showHeaderActions = false,
  randomWidths = true,
  showPagination = false,
  columnWidths,
}: TableSkeletonProps) {
  const getWidthClass = (rowIndex: number, colIndex: number) => {
    if (!randomWidths) return 'w-full'
    const pattern = CELL_WIDTH_PATTERNS[rowIndex % CELL_WIDTH_PATTERNS.length]
    return pattern[colIndex % pattern.length]
  }

  const getHeaderWidth = (colIndex: number) => {
    return HEADER_WIDTHS[colIndex % HEADER_WIDTHS.length]
  }

  // Exact mode: mirror the real table's column geometry so the swap to live
  // data causes no layout shift
  const isExact = !!columnWidths && columnWidths.length > 0
  const columnCount = isExact ? columnWidths.length : columns
  const toCssWidth = (width: number | string) =>
    typeof width === 'number' ? `${width}px` : width

  return (
    <div className={cn('w-full', className)}>
      {/* Table skeleton that matches actual table appearance */}
      <div className="overflow-hidden bg-background">
        {/* Optional action bar */}
        {showHeaderActions && (
          <div className="flex items-center justify-between border-b border-border/50 bg-muted/20 px-4 py-3">
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-48" variant="table" />
              <Skeleton className="h-8 w-24" variant="table" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-8 w-8" variant="table" />
              <Skeleton className="h-8 w-8" variant="table" />
            </div>
          </div>
        )}

        <table
          className={cn(
            'border-collapse',
            isExact ? 'table-fixed' : 'w-full table-fixed',
          )}
          style={
            isExact ? { width: 'max-content', minWidth: '100%' } : undefined
          }
        >
          {/* Match the real table's column geometry when widths are known */}
          {isExact && (
            <colgroup>
              {columnWidths!.map((width, index) => (
                <col
                  key={`col-${index}`}
                  style={{ width: toCssWidth(width) }}
                />
              ))}
            </colgroup>
          )}
          {/* Header skeleton */}
          {showHeader && (
            <thead>
              <tr className="border-b border-border/50 bg-background">
                {Array.from({ length: columnCount }).map((_, index) => (
                  <th
                    key={`header-${index}`}
                    className={cn(
                      'px-3 py-2.5 text-left',
                      // The real table header height (design audit S12)
                      TABLE_HEADER_HEIGHT_CLASS,
                      index < columnCount - 1 && 'border-r border-border/40',
                    )}
                  >
                    <Skeleton
                      className={cn('h-3', getHeaderWidth(index))}
                      variant="table"
                      intensity="subtle"
                    />
                  </th>
                ))}
              </tr>
            </thead>
          )}

          {/* Body rows skeleton */}
          <tbody>
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <tr
                key={`row-${rowIndex}`}
                className="border-b border-border/40 bg-background"
              >
                {Array.from({ length: columnCount }).map((_, colIndex) => (
                  <td
                    key={`cell-${rowIndex}-${colIndex}`}
                    className={cn(
                      'px-3 py-2',
                      TABLE_ROW_HEIGHT_CLASS,
                      colIndex < columnCount - 1 && 'border-r border-border/40',
                    )}
                  >
                    <Skeleton
                      className={cn(
                        'h-4',
                        colIndex === 0
                          ? 'w-3/4'
                          : getWidthClass(rowIndex, colIndex),
                      )}
                      variant="table"
                      intensity={colIndex === 0 ? 'light' : 'subtle'}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Footer with pagination skeleton, only when the table paginates */}
        {showPagination && (
          <div className="flex items-center justify-between border-t border-border/50 bg-background px-4 py-3">
            <Skeleton className="h-4 w-32" variant="table" intensity="subtle" />
            <div className="flex items-center gap-1">
              <Skeleton
                className="h-8 w-8"
                variant="table"
                intensity="subtle"
              />
              <Skeleton
                className="h-8 w-8"
                variant="table"
                intensity="subtle"
              />
              <Skeleton
                className="h-8 w-8"
                variant="table"
                intensity="subtle"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/** Compact table skeleton for smaller spaces */
export function CompactTableSkeleton({
  columns = 4,
  rows = 5,
  className,
}: Omit<TableSkeletonProps, 'showHeader' | 'showHeaderActions'>) {
  return (
    <div className={cn('w-full space-y-2', className)}>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div
          key={`row-${rowIndex}`}
          className="rounded-card flex items-center gap-3 border border-border/20 bg-muted/20 p-3"
        >
          <Skeleton
            className="h-8 w-8 flex-shrink-0 rounded-lg"
            intensity="subtle"
          />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3.5 w-3/4 rounded" />
            <Skeleton className="h-3 w-1/2 rounded" intensity="subtle" />
          </div>
          <Skeleton className="h-6 w-16 rounded-full" intensity="subtle" />
        </div>
      ))}
    </div>
  )
}

/** Grid skeleton for card layouts */
export function GridSkeleton({
  items = 6,
  columns = 3,
  className,
}: {
  items?: number
  columns?: number
  className?: string
}) {
  return (
    <div
      className={cn(
        'grid gap-4',
        columns === 2 && 'grid-cols-2',
        columns === 3 && 'grid-cols-3',
        columns === 4 && 'grid-cols-4',
        className,
      )}
    >
      {Array.from({ length: items }).map((_, index) => (
        <div
          key={`card-${index}`}
          className="space-y-3 rounded-xl border border-border/30 bg-card p-4"
        >
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-lg" variant="rectangular" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4 rounded" />
              <Skeleton className="h-3 w-1/2 rounded" intensity="subtle" />
            </div>
          </div>
          <Skeleton className="h-20 w-full rounded-lg" intensity="subtle" />
          <div className="flex items-center justify-between pt-2">
            <Skeleton className="h-6 w-20 rounded-full" intensity="subtle" />
            <Skeleton className="h-6 w-6 rounded-full" intensity="subtle" />
          </div>
        </div>
      ))}
    </div>
  )
}
